import { Navigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { usePermissions } from "../contexts/PermissionsContext";
import { savePostLoginRedirect } from "../utils/postLoginRedirect";

// Every non-Agent/Curator/SuperAdmin role gets the same manager-panel access today —
// there's no fine-grained permission system, so all org-tier roles share this list.
const MANAGER_PANEL_ROLES = [
  "Manager",
  "Admin",
  "Curator",
  "TeamLeader",
  "QualityAnalyst",
  "Trainer",
  "AssistantManagerTraining",
  "ManagerTraining",
  "SeniorManagerTraining",
  "AssistantManagerQuality",
  "ManagerQuality",
  "SeniorManagerQuality",
  "AssistantManagerOps",
  "ManagerOps",
  "SeniorManagerOps",
  "AssistantVicePresident",
  "SeniorVicePresident",
  "AssistantManager",
  "SeniorAssistantManager",
  "SeniorTrainer",
  "SeniorManager",
];

// requiredPage checks the caller's live-resolved page permissions (see
// PermissionsContext) instead of a hardcoded role list — used for /user/* manager-panel
// routes. allowedRoles keeps working exactly as before for /agent/*, /super-admin/*, and
// /demo/* routes. When BOTH are given (e.g. /user/audio-analytics/:id, shared with the
// Agent's own view), access is granted if EITHER check passes — the role check doesn't
// need to wait on the permissions fetch, so an Agent isn't blocked by loading state.
// requireBoth flips that to a real AND: both the role AND the granted page permission are
// required — used for the Agent-only pages that mirror a manager-panel page (e.g.
// /agent/dashboard mirrors /user/dashboard) so they're gated by the exact same permission
// instead of always being reachable by any Agent regardless of what's been granted.
export const ProtectedRoute = ({ element, allowedRoles, requiredPage, requireBoth }) => {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const { pages, loading } = usePermissions() || {};

  if (!token) {
    // Bring the user back here after they log in (see Login.jsx).
    savePostLoginRedirect(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  const decodedToken = jwtDecode(token);
  const roleAllowed = !allowedRoles || allowedRoles.includes(decodedToken?.userRole);

  if (requiredPage) {
    if (!requireBoth && allowedRoles && roleAllowed) return element;
    if (loading) return null;
    if (!pages?.[requiredPage] || (requireBoth && !roleAllowed)) {
      return <Navigate to={location.state?.from?.pathname || "/"} replace />;
    }
    return element;
  }

  if (!roleAllowed) {
    return <Navigate to={location.state?.from?.pathname || "/"} replace />;
  }

  return element;
};

// Landing page picked per resolved permission, in priority order. agentProfile is
// deliberately excluded — it always needs an :id and can't be a landing page on its own.
const LANDING_PAGE_BY_PERMISSION = {
  dashboard: "/user/dashboard",
  interactions: "/user/audio-details",
  leaderboard: "/user/leaderboard",
  training: "/user/training",
  userManagement: "/user/user-management",
  agentAssignment: "/user/agent-assignment",
  myProfile: "/user/setting",
  accountSettings: "/user/account/manage",
  knowledgePortal: "/user/knowledge-portal",
  sensitiveKeywordAlerts: "/user/sensitive-keyword-alerts",
  goodCallLibrary: "/user/good-call-library",
};

export const RoleBasedRedirect = () => {
  const token = localStorage.getItem("token");
  const decodedToken = token ? jwtDecode(token) : "";
  const { pages, loading } = usePermissions() || {};

  // Logged out — straight to login, rather than via the Guest demo route below (whose
  // ProtectedRoute would otherwise record it as the post-login redirect).
  if (!token) return <Navigate to="/login" replace />;

  if (decodedToken?.userRole == "SuperAdmin")
    return <Navigate to="/super-admin/process-management" replace />;
  if (decodedToken?.userRole == "Guest")
    return <Navigate to="/demo/audio/upload-file" replace />;

  const isAgent = decodedToken?.userRole == "Agent";

  if (!isAgent && !MANAGER_PANEL_ROLES.includes(decodedToken?.userRole)) {
    return (
      <div>
        No valid role. Redirecting to login...
        <Navigate to="/demo/audio/upload-file" replace />
      </div>
    );
  }

  // Manager-panel role (or an Agent once granted manager-panel pages) — wait for resolved
  // permissions before picking a landing page, so we don't briefly redirect somewhere this
  // user can't actually access.
  if (loading) return null;

  const interactionLandingCompanies = [
    "67e560a6ae2e0d3f96b5eb09",
    "69ba88379b1aea31bda6224e"
  ];
  if (pages?.interactions && interactionLandingCompanies.includes(decodedToken?.companyId)) {
    return <Navigate to="/user/audio-details" replace />;
  }

  const firstEnabledKey = Object.keys(LANDING_PAGE_BY_PERMISSION).find((key) => pages?.[key]);
  if (firstEnabledKey) {
    return <Navigate to={LANDING_PAGE_BY_PERMISSION[firstEnabledKey]} replace />;
  }

  return (
    <div>
      Your role currently has no pages enabled. Contact an Admin to update your permissions.
    </div>
  );
};
